import { ConfigurationType, DensityAccount, SpaceHierarchy } from "../types";

import { Images } from "../resources";

const getUserAccount = (id: string): DensityAccount => {
  switch (id) {
    case "org_717836024721441650":
      return DensityAccount.CBRE;
    case "org_693132492059705625":
      return DensityAccount.SPLUNK;
    case "org_560187435527438374":
      return DensityAccount.DELTA;
    case "org_877253134496302034":
    case "org_895715382826893521":
      return DensityAccount.BROOKFIELD;
    case "org_874771034023133419":
      return DensityAccount.NETFLIX;
    case "org_761743421990764583":
      return DensityAccount.TWITTER;
    case "org_680799147153949152":
      return DensityAccount.REKOM;
    case "org_464156814699659649":
      return DensityAccount.VERIZON;
    case "org_886427186108564477":
      return DensityAccount.OKTA;
    case "org_577963138448621594":
      return DensityAccount.DENSITY;
    case "org_888112136998355313":
      return DensityAccount.MEDALLIA;
    case "org_241255233743749376":
      return DensityAccount.DENSITY_DEV;
    case "org_924379250436341856":
      return DensityAccount.BMS;
    case "org_733492776376730289":
      return DensityAccount.ACME_INC;
    case "org_xxxxxxxxxxxxxxxxxx":
      return DensityAccount.CBRE;
    default:
      return DensityAccount.OTHER;
  }
};

const demoFilter = (spaces: SpaceHierarchy[]) => {
  const campusName = "*CBRE Offices Data";

  // Grab the Signal Campus
  const campuses = spaces.filter((space) => {
    return space.name === campusName;
  });

  if (campuses.length === 0) {
    return [];
  }
  const campus = campuses[0];

  // Get the IDs for the Signal Campus
  const spaceIds = campus["children"]?.map((buildingInfo) => {
    return buildingInfo.id;
  });

  return spaceIds;
};

const cbreFilter = (spaces: SpaceHierarchy[]) => {
  const campusName = "*CBRE Offices Data";

  // Grab the Signal Campus
  const campuses = spaces.filter((space) => {
    return space.name === campusName;
  });

  if (campuses.length === 0) {
    return [];
  }
  const campus = campuses[0];

  // Get the IDs for the Signal Campus
  const spaceIds = campus["children"]?.map((buildingInfo) => {
    return buildingInfo.id;
  });

  return spaceIds;
};

const bmsFilter = (spaces: SpaceHierarchy[]) => {
  let buildings = findAllSubSpaces(spaces);
  return buildings
    .map((building: SpaceHierarchy) => {
      return building.id;
    })
    .filter(
      (id: string) =>
        id === "spc_944076622288388189" ||
        id === "spc_944076494475362392" ||
        id === "spc_944076857102303328" ||
        id === "spc_944076914962727010" ||
        id === "spc_940719289273417866" ||
        id === "spc_944075904412287047" ||
        id === "spc_944076025036275788" ||
        id === "spc_944076104124072014" ||
        id === "spc_944077099390468212" ||
        id === "spc_942087565702332442" ||
        id === "spc_944077314994471038" ||
        id === "spc_944077258220372086"
    );
};

const findAllBuildings = (spaces: SpaceHierarchy[] | null) => {
  if (spaces === null) {
    return [];
  }
  let buildings: SpaceHierarchy[] = [];
  for (let i = 0; i < spaces.length; i++) {
    let space = spaces[i];
    if (space.space_type === "building") {
      buildings.push(space);
    }
    if ("children" in space) {
      let children = findAllBuildings(space.children);
      buildings = buildings.concat(children);
    }
  }
  return buildings;
};

const findAllLounges = (spaces: SpaceHierarchy[] | null) => {
  if (spaces === null) {
    return [];
  }
  let lounges: SpaceHierarchy[] = [];

  for (let i = 0; i < spaces.length; i++) {
    let space = spaces[i];
    if (space.space_type === "space") {
      lounges.push(space);
    }
    if ("children" in space) {
      let children = findAllLounges(space.children);
      lounges = lounges.concat(children);
    }
  }
  return lounges;
};

const findAllSubSpaces = (spaces: SpaceHierarchy[] | null) => {
  if (spaces === null) {
    return [];
  }
  let subSpace: SpaceHierarchy[] = [];

  for (let i = 0; i < spaces.length; i++) {
    let space = spaces[i];
    subSpace.push(space);
    if ("children" in space) {
      let children = findAllSubSpaces(space.children);
      subSpace = subSpace.concat(children);
    }
  }
  return subSpace;
};

const deltaFilter = (spaces: SpaceHierarchy[]) => {
  let lounges = findAllLounges(spaces);
  return lounges.map((lounge) => {
    return lounge.id;
  });
};

const netflixFilter = (spaces: SpaceHierarchy[]) => {
  let buildings = findAllBuildings(spaces);
  const buildingIds = buildings.map((building: SpaceHierarchy) => {
    return building.id;
  });
  return [
    ...buildingIds,
    "spc_885702025990373629",
    "spc_890414837446738802",
    "spc_885578552601215562",
  ];
};

const twitterFilter = (spaces: SpaceHierarchy[]) => {
  let buildings = findAllSubSpaces(spaces);
  return ["spc_940988500503691824", "spc_938895355901116819"];
};

const oktaFilter = (spaces: SpaceHierarchy[]) => {
  let buildings = findAllSubSpaces(spaces);
  return buildings
    .map((building: SpaceHierarchy) => {
      return building.id;
    })
    .filter(
      (id: string) =>
        id === "spc_913513652017955744" ||
        id === "spc_913501216724485044" ||
        id === "spc_959973949536469851"
    );
};

const rekomFilter = (spaces: SpaceHierarchy[]) => {
  let buildings = findAllBuildings(spaces);
  return buildings.map((building: SpaceHierarchy) => {
    return building.id;
  });
};

const splunkFilter = (spaces: SpaceHierarchy[]) => {
  let buildings: SpaceHierarchy[] = [];
  for (let i = 0; i < spaces.length; i++) {
    let space = spaces[i];

    buildings = [...buildings, ...findAllBuildings(space.children)];
  }
  return buildings.map((building: SpaceHierarchy) => {
    return building.id;
  });
};

const brookfieldFilter = (spaces: SpaceHierarchy[]) => {
  let buildings = findAllBuildings(spaces);
  return buildings.map((building: SpaceHierarchy) => {
    return building.id;
  });
};

const verizonFilter = (spaces: SpaceHierarchy[]) => {
  let buildings = findAllBuildings(spaces);
  return buildings.map((building: SpaceHierarchy) => {
    return building.id;
  });
};

const medalliaFilter = (spaces: SpaceHierarchy[]) => {
  let buildings = findAllBuildings(spaces);
  return buildings.map((building: SpaceHierarchy) => {
    return building.id;
  });
  // .filter((id: string) => id === "spc_900112113375118027");
};

const densityFilter = (spaces: SpaceHierarchy[]) => {
  let buildings = findAllBuildings(spaces);
  return buildings.map((building: SpaceHierarchy) => {
    return building.id;
  });
};

const densityDevFilter = (spaces: SpaceHierarchy[]) => {
  let buildings = findAllBuildings(spaces);
  return buildings.map((building: SpaceHierarchy) => {
    return building.id;
  });
};

const acmeFilter = (spaces: SpaceHierarchy[]) => {
  let buildings = findAllBuildings(spaces);
  return buildings.map((building: SpaceHierarchy) => {
    return building.id;
  });
};

const otherFilter = (spaces: SpaceHierarchy[]) => {
  return [];
};

// Customer IDs
let accountIds: ConfigurationType = {};
accountIds[DensityAccount.CBRE] = {
  usr: "usr_936330766948761604",
  imageUrl: Images.cbre,
  filter: cbreFilter,
};
accountIds[DensityAccount.SPLUNK] = {
  usr: "usr_711244301648003159",
  imageUrl: Images.splunk,
  filter: splunkFilter,
};
accountIds[DensityAccount.DELTA] = {
  usr: "usr_560188392671805486",
  imageUrl: Images.delta,
  filter: deltaFilter,
};
accountIds[DensityAccount.BROOKFIELD] = {
  usr: "usr_885000495196799980",
  imageUrl: Images.brookfield,
  filter: brookfieldFilter,
};
accountIds[DensityAccount.NETFLIX] = {
  usr: "usr_885226615116660805",
  imageUrl: Images.netflix,
  filter: netflixFilter,
};
accountIds[DensityAccount.TWITTER] = {
  usr: "usr_948597737811608120",
  imageUrl: Images.twitter,
  filter: twitterFilter,
};
accountIds[DensityAccount.REKOM] = {
  usr: "usr_922782244278371046",
  imageUrl: Images.rekom,
  filter: rekomFilter,
};
accountIds[DensityAccount.VERIZON] = {
  usr: "usr_622109398067577025",
  imageUrl: Images.verizon,
  filter: verizonFilter,
};
accountIds[DensityAccount.OKTA] = {
  usr: "usr_921172179045843112",
  imageUrl: Images.okta,
  filter: oktaFilter,
};
accountIds[DensityAccount.MEDALLIA] = {
  usr: "usr_905861728233849180",
  imageUrl: Images.medallia,
  filter: medalliaFilter,
};
accountIds[DensityAccount.BMS] = {
  usr: "usr_943924240191586867",
  imageUrl: Images.bms,
  filter: bmsFilter,
};
accountIds[DensityAccount.DENSITY] = {
  usr: "usr_507257145167708708",
  imageUrl: Images.density,
  filter: densityFilter,
};
accountIds[DensityAccount.DENSITY_DEV] = {
  usr: "usr_247561038487093560",
  imageUrl: Images.density,
  filter: densityDevFilter,
};
accountIds[DensityAccount.ACME_INC] = {
  usr: "usr_733494325723267293",
  imageUrl: "",
  filter: acmeFilter,
};
accountIds[DensityAccount.DEMO] = {
  usr: "usr_936330766948761604",
  imageUrl: "",
  filter: demoFilter,
};
accountIds[DensityAccount.OTHER] = {
  usr: "usr_xxxxxxxxxxxxxxxxxx",
  imageUrl: "",
  filter: otherFilter,
};

export { accountIds, getUserAccount };
